// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  ScrollText, ChevronLeft, ChevronRight, AlertCircle, Filter
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Action Badge - color coded by action type - Uses VCIN brand blue for auth actions
function ActionBadge({ action }) {
  const getActionStyle = (action) => {
    if (action.includes('LOGIN') || action.includes('VERIFIED')) {
      return { bg: `${VCIN_COLORS.primary}15`, text: VCIN_COLORS.primary };
    }
    if (action.includes('CREATED') || action.includes('APPROVED') || action.includes('REACTIVATED')) {
      return { bg: '#E8F5E9', text: '#2E7D32' };
    }
    if (action.includes('SUSPENDED') || action.includes('REDACTED') || action.includes('SUPPRESSED') || action.includes('FAILED')) {
      return { bg: '#FFEBEE', text: '#C62828' };
    }
    if (action.includes('UPDATED') || action.includes('OVERRIDDEN')) {
      return { bg: '#FFF3E0', text: '#E65100' };
    }
    return { bg: '#F5F5F5', text: '#616161' };
  };

  const style = getActionStyle(action);
  
  return (
    <span 
      className="px-2 py-1 rounded text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {action.replace(/_/g, ' ')}
    </span>
  );
}

// Table Skeleton
function TableSkeleton() {
  return (
    <tbody className="divide-y divide-gray-100">
      {[...Array(10)].map((_, i) => (
        <tr key={i} className="animate-pulse">
          <td className="px-6 py-4"><div className="h-4 w-32 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-5 w-40 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-36 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-24 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-20 bg-gray-200 rounded" /></td>
          <td className="px-6 py-4"><div className="h-4 w-28 bg-gray-200 rounded" /></td>
        </tr>
      ))}
    </tbody>
  );
}

export default function SAAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters - READ ONLY operations only
  const [filters, setFilters] = useState({
    action: '',
    date_from: '',
    date_to: ''
  });

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 50 });
      
      if (filters.action) params.append('action', filters.action);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setActionTypes(response.data.action_types || []);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchLogs(1);
    setShowFilters(false);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  if (error && !loading) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-audit-log-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-1">Read-only system audit trail • {pagination.total} entries</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors border bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
          style={activeFilterCount > 0 ? { backgroundColor: `${VCIN_COLORS.primary}10`, borderColor: VCIN_COLORS.primary, color: VCIN_COLORS.primary } : {}}
          data-testid="audit-filter-btn"
        >
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span 
              className="ml-1 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Read-Only Notice */}
      <div 
        className="rounded-lg p-4 mb-6 flex items-center gap-3"
        style={{ backgroundColor: `${VCIN_COLORS.primary}10`, border: `1px solid ${VCIN_COLORS.primary}30` }}
      >
        <ScrollText className="w-5 h-5 flex-shrink-0" style={{ color: VCIN_COLORS.primary }} />
        <div className="text-sm" style={{ color: VCIN_COLORS.primary }}>
          <span className="font-semibold">Append-only log:</span> This audit trail is read-only and cannot be modified or deleted. 
          All platform actions are recorded permanently for compliance and security purposes.
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200" data-testid="audit-filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Actions</option>
                {actionTypes.map(action => (
                  <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setFilters({ action: '', date_from: '', date_to: '' })}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Reset
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg"
              style={{ backgroundColor: VCIN_COLORS.primary }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Table - NO EDIT BUTTONS */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-log-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Collection</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Record ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IP Address</th>
              </tr>
            </thead>
            
            {loading ? (
              <TableSkeleton />
            ) : logs.length === 0 ? (
              <tbody>
                <tr>
                  <td colSpan="6" className="p-12 text-center">
                    <ScrollText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="font-medium text-gray-600">No audit logs found</p>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(log.timestamp).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ActionBadge action={log.action} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{log.user_email}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 font-mono">{log.collection}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs text-gray-500 font-mono">
                        {log.record_id?.toString().slice(-12) || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500 font-mono">{log.ip_address || '-'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* Pagination - READ ONLY navigation */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * 50 + 1} to {Math.min(pagination.page * 50, pagination.total)} of {pagination.total} entries
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">Page {pagination.page} of {pagination.pages}</span>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
